/**
 * EbuyTab — "eBuy" tab inside Marketplace Sentinel.
 *
 * v3.18.30 — Redesigned to match SAM Sentinel table layout:
 *   - Filter bar (search, tier, status, count, refresh) above a full-width table
 *   - 460 px detail pane slides in on row click, closed with ×
 *   - Sync button lives in the parent header (unchanged)
 *
 * Folder picker strategy:
 *   - Chromium / Brave: window.showDirectoryPicker()
 *   - Firefox / Safari: <input type="file" webkitdirectory> fallback
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  RefreshCw, Loader2, ExternalLink, CheckCircle2,
  AlertCircle, FileText, Archive, Calendar, Building2, Shield, X,
  Trophy, Medal, Award, Search, ChevronRight, XCircle,
  ChevronsUpDown, ChevronUp, ChevronDown,
  OctagonX, CircleCheck, Brain,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { AI_RANK_ORDER } from '../../lib/aiRank';
import { describeApiError, readFetchError } from '../../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_BADGE = {
  HIGH:   { label: 'Gold',   icon: Trophy, badgeClass: 'bg-amber-100 text-amber-700 border border-amber-200',   iconBg: 'bg-amber-100 text-amber-600' },
  MEDIUM: { label: 'Silver', icon: Medal,  badgeClass: 'bg-slate-200 text-slate-700 border border-slate-400',   iconBg: 'bg-slate-200 text-slate-600' },
  LOW:    { label: 'Bronze', icon: Award,  badgeClass: 'bg-orange-100 text-orange-700 border border-orange-200', iconBg: 'bg-orange-100 text-orange-600' },
};

const STATUS_CHIP = {
  NEW:       { label: 'New',       cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
  UPDATED:   { label: 'Updated',   cls: 'bg-purple-100 text-purple-700 border border-purple-200' },
  GONE:      { label: 'Closed',    cls: 'bg-slate-100 text-slate-500 border border-slate-200' },
  DISMISSED: { label: 'Dismissed', cls: 'bg-slate-100 text-slate-500 border border-slate-200' },
  CLAIMED:   { label: 'Claimed',   cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
};

const AI_RANK_CONFIG = {
  BLUE:   { label: 'Blue',   cls: 'bg-blue-600 text-white',    tip: '≥50% requirements met — strong fit'  },
  GREEN:  { label: 'Green',  cls: 'bg-green-600 text-white',   tip: '≥40% requirements met — good fit'    },
  YELLOW: { label: 'Yellow', cls: 'bg-yellow-500 text-white',  tip: '≥30% requirements met — moderate fit'},
  RED:    { label: 'Red',    cls: 'bg-red-600 text-white',     tip: '≥20% requirements met — weak fit'    },
  GRAY:   { label: 'Gray',   cls: 'bg-slate-400 text-white',   tip: '<20% requirements met — poor fit'    },
};

// ─────────────────────────────────────────────────────────────────────────────
export default forwardRef(function EbuyTab(
  { authToken, user, isConsultant, selectedClientId, clientOptions, clientBrandColors, onSyncStateChange, onLatestSyncChange },
  ref
) {
  // Target org: consultant → scoped to selected client; contractor → own org
  const targetOrgId = isConsultant && selectedClientId ? selectedClientId : null;

  // Resolve the selected client's 3-char eBuy folder code (e.g. "CEC").
  // Returns '' if none configured. Used to navigate scans/<CODE>/ and
  // read state_<CODE>.json under the new per-client eBuyExtract layout.
  const resolveClientCode = useCallback(async () => {
    if (!isConsultant || !selectedClientId) return '';
    try {
      const res = await fetch(`${API_URL}/api/clients/${selectedClientId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) return '';
      const data = await res.json();
      return (data?.ebuy_client_code || '').trim().toUpperCase();
    } catch {
      return '';
    }
  }, [isConsultant, selectedClientId, authToken]);

  const [loading, setLoading]           = useState(true);
  const [syncing, setSyncing]           = useState(false);
  const [rankingEbuy, setRankingEbuy]   = useState(false);
  const [syncPhase, setSyncPhase]       = useState('');   // '' | 'metadata' | 'files'
  const [fileProgress, setFileProgress] = useState('');   // e.g. 'Uploading files 3/8…'
  const [opps, setOpps]                 = useState([]);
  const [latestSync, setLatestSync]     = useState(null);
  const [tierFilter, setTierFilter]     = useState('all');
  // Server-side, like tier/status — the list is capped at `limit`, so filtering
  // here would only narrow the rows already fetched.
  const [aiRankFilter, setAiRankFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('NEW');
  const [selectedRfq, setSelectedRfq]   = useState(null);
  const [searchQuery, setSearchQuery]   = useState('');

  // ── Sort state ────────────────────────────────────────────────────────────
  const [eBuySortField, setEBuySortField] = useState('request_close');
  const [eBuySortDir,   setEBuySortDir]   = useState('asc');

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchOpps = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (targetOrgId) params.append('organization_id', targetOrgId);
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      if (aiRankFilter !== 'all') params.append('ai_rank', aiRankFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '1500');  // load accumulated RFQs (days of sync) into the scroll list
      const res = await fetch(`${API_URL}/api/ebuy/opportunities?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const list = body.opportunities || [];
      setOpps(list);
      // Auto-open the first RFQ's detail (parity with the SAM feed).
      setSelectedRfq(prev => prev || list[0] || null);
      setLatestSync(body.latest_sync || null);
    } catch (e) {
      console.error('eBuy fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [authToken, targetOrgId, tierFilter, statusFilter, aiRankFilter]);

  useEffect(() => { fetchOpps(); }, [fetchOpps]);

  // Close the open detail when the client changes so it doesn't linger on the
  // previous client's RFQ; the refetch above then auto-opens the new first one.
  useEffect(() => { setSelectedRfq(null); }, [targetOrgId]);

  // ── Sync helpers ───────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);

  // ── Phase 1: metadata (CSV required, state.json optional) ───────────────
  // extraCsvFiles: [{name, file}] — all historical scan CSVs except the latest.
  // Backend merges them so the full cumulative history is processed, not just
  // the scraper's latest delta.
  const ingestMetadata = useCallback(async (stateFile, csvFile, csvName, extraCsvFiles = []) => {
    const formData = new FormData();
    // state.json is optional — only append if present (provides attachment metadata)
    if (stateFile) formData.append('state_json', stateFile, 'state.json');
    formData.append('scan_csv', csvFile, csvName || 'scan.csv');
    // Send all historical CSVs oldest-first so latest always wins on conflicts
    for (const { name, file } of extraCsvFiles) {
      formData.append('extra_scans', file, name);
    }
    if (targetOrgId) formData.append('organization_id', targetOrgId);
    const res = await fetch(`${API_URL}/api/ebuy/ingest`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    });
    if (!res.ok) {
      const msg = await readFetchError(res, `HTTP ${res.status}`);
      throw new Error(msg);
    }
    return res.json();
  }, [authToken, targetOrgId]);

  // ── Phase 2: upload attachment files in 60 MB batches ────────────────────
  // Each entry in `taggedFiles` is { file: File, rid: string, field: 'archives'|'mras_archives' }
  const uploadAttachmentBatches = useCallback(async (taggedFiles) => {
    if (!taggedFiles.length) return { ebuy: 0, mras: 0 };

    const BATCH_BYTES = 60 * 1024 * 1024; // 60 MB
    let batch       = new FormData();
    let batchBytes  = 0;
    let batchNum    = 0;
    let totalEbuy   = 0;
    let totalMras   = 0;

    const flush = async (fd) => {
      if (targetOrgId) fd.append('organization_id', targetOrgId);
      const r = await fetch(`${API_URL}/api/ebuy/attachments/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      if (!r.ok) { const m = await readFetchError(r, `HTTP ${r.status}`); throw new Error(m); }
      return r.json();
    };

    for (let i = 0; i < taggedFiles.length; i++) {
      const { file, rid, field } = taggedFiles[i];
      // Rename with the convention the backend expects: <rid>__<filename>
      const namedFile = new File([file], `${rid}__${file.name}`, { type: file.type });
      batch.append(field, namedFile);
      batchBytes += file.size;

      if (batchBytes >= BATCH_BYTES || i === taggedFiles.length - 1) {
        batchNum++;
        const totalBatches = Math.ceil(
          taggedFiles.reduce((s, f) => s + f.file.size, 0) / BATCH_BYTES
        );
        setFileProgress(`Uploading files — batch ${batchNum}/${Math.max(batchNum, totalBatches)}…`);
        const result = await flush(batch);
        totalEbuy += result.ebuy_uploaded || 0;
        totalMras += result.mras_uploaded || 0;
        batch      = new FormData();
        batchBytes = 0;
      }
    }
    return { ebuy: totalEbuy, mras: totalMras };
  }, [authToken, targetOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Collect attachment files from a dirHandle (Brave / Chromium) ──────────
  const collectFilesFromDir = useCallback(async (dirHandle, ridSet) => {
    const tagged = [];
    for (const [subDirName, formField] of [['MAS', 'archives'], ['MRAS', 'mras_archives']]) {
      let subDir;
      try { subDir = await dirHandle.getDirectoryHandle(subDirName); }
      catch { continue; }

      for await (const [folderName, folderHandle] of subDir) {
        if (folderHandle.kind !== 'directory') continue;
        // Match folder name to a RID: folder names are typically "{rid}_{title}"
        let rid = null;
        for (const r of ridSet) {
          if (folderName === r || folderName.startsWith(r + '_') || folderName.startsWith(r + ' ')) {
            rid = r; break;
          }
        }
        if (!rid) {
          // Fallback: first underscore-delimited segment
          const candidate = folderName.split('_')[0];
          if (ridSet.has(candidate)) rid = candidate;
        }
        if (!rid) continue;

        for await (const [, fileHandle] of folderHandle) {
          if (fileHandle.kind === 'file') {
            const file = await fileHandle.getFile();
            tagged.push({ file, rid, field: formField });
          }
        }
      }
    }
    return tagged;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Collect attachment files from a FileList (Firefox webkitdirectory) ───
  const collectFilesFromList = useCallback((allFiles, ridSet) => {
    const tagged = [];
    for (const file of allFiles) {
      const parts = file.webkitRelativePath.split('/');
      // Expected path: <root>/<MAS|MRAS>/<folder>/<filename>
      if (parts.length < 4) continue;
      const subDirName = parts[1].toUpperCase();
      const formField  = subDirName === 'MRAS' ? 'mras_archives' : 'archives';
      const folderName = parts[2];

      let rid = null;
      for (const r of ridSet) {
        if (folderName === r || folderName.startsWith(r + '_') || folderName.startsWith(r + ' ')) {
          rid = r; break;
        }
      }
      if (!rid) {
        const candidate = folderName.split('_')[0];
        if (ridSet.has(candidate)) rid = candidate;
      }
      if (rid) tagged.push({ file, rid, field: formField });
    }
    return tagged;
  }, []);

  // ── Orchestrator: runs Phase 1 then Phase 2 ───────────────────────────────
  const runSync = useCallback(async (stateFile, csvFile, csvName, phase2Source, extraCsvFiles = []) => {
    setSyncing(true);
    setSyncPhase('metadata');
    setFileProgress('');
    try {
      // Phase 1 — parse state.json for RID set if available (attachment metadata only)
      let ridSet = new Set();
      if (stateFile) {
        try {
          const stateData = JSON.parse(await stateFile.text());
          ridSet = new Set(Object.keys(stateData.opps || {}));
        } catch { /* ignore — state.json is optional */ }
      }

      const body = await ingestMetadata(stateFile, csvFile, csvName, extraCsvFiles);
      toast.success(`Sync complete: ${body.new} new · ${body.updated} updated · ${body.gone} closed`);

      // Warn if rows were dropped by the contract-number filter
      if (body.rows_filtered > 0) {
        const contractList = (body.filtered_contract_numbers || []).join(', ');
        toast.warning(
          `${body.rows_filtered} of ${body.rows_in_csv} rows filtered out`,
          {
            description: `Contract number(s) not in your GSA Schedule config: ${contractList || 'unknown'}. `
              + `Go to eBuy Profile → GSA Schedule Contracts to add them, or clear the list to import all.`,
            duration: 12000,
          },
        );
      }

      await fetchOpps();

      // Phase 2 — upload attachment files (best-effort, non-blocking on error)
      if (ridSet.size > 0 && phase2Source) {
        setSyncPhase('files');
        let tagged = [];
        if (typeof phase2Source.getDirectoryHandle === 'function') {
          // dirHandle from showDirectoryPicker
          tagged = await collectFilesFromDir(phase2Source, ridSet);
        } else {
          // FileList from <input webkitdirectory>
          tagged = collectFilesFromList(phase2Source, ridSet);
        }

        if (tagged.length > 0) {
          const totalMB = (tagged.reduce((s, t) => s + t.file.size, 0) / (1024 * 1024)).toFixed(1);
          setFileProgress(`Uploading ${tagged.length} files (${totalMB} MB)…`);
          try {
            const { ebuy, mras } = await uploadAttachmentBatches(tagged);
            const total = ebuy + mras;
            if (total > 0) toast.success(`Files uploaded: ${ebuy} eBuy + ${mras} MRAS stored for team access.`);
          } catch (uploadErr) {
            console.error('[eBuy file-upload] error', uploadErr);
            toast.error(`File upload failed: ${uploadErr.message}`, { duration: 8000 });
          }
        }
      }
    } catch (e) {
      console.error('[eBuy folder-sync] error', e);
      toast.error(describeApiError(e, 'eBuy sync failed'), { duration: 10000 });
    } finally {
      setSyncing(false);
      setSyncPhase('');
      setFileProgress('');
    }
  }, [ingestMetadata, fetchOpps, collectFilesFromDir, collectFilesFromList, uploadAttachmentBatches]); // eslint-disable-line react-hooks/exhaustive-deps

  // Firefox / Safari fallback
  const handleFileInputChange = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    if (isConsultant && !selectedClientId) {
      toast.error('Select a client organization before syncing eBuy.', { duration: 6000 });
      return;
    }
    const clientCode = await resolveClientCode();
    if (isConsultant && selectedClientId && !clientCode) {
      toast.error(
        'This client has no eBuy Folder Code. Set the 3-char code on the Client Profile, then sync again.',
        { duration: 10000 },
      );
      return;
    }

    // Per-client state file: state_<CODE>.json preferred, else state.json
    const stateFile = files.find(f => {
      const base = f.webkitRelativePath.split('/').pop();
      return clientCode ? base === `state_${clientCode}.json` : base === 'state.json';
    }) || files.find(f => f.webkitRelativePath.split('/').pop() === 'state.json') || null;

    // CSVs — prefer those under scans/<CODE>/; fall back to any scan_*.csv
    let scanFiles = files.filter(f =>
      f.name.startsWith('scan_') && f.name.endsWith('.csv'),
    );
    if (clientCode) {
      const scoped = scanFiles.filter(f =>
        f.webkitRelativePath.includes(`/scans/${clientCode}/`)
        || f.webkitRelativePath.includes(`scans/${clientCode}/`),
      );
      if (scoped.length) scanFiles = scoped;
    }
    scanFiles = scanFiles.sort((a, b) => b.name.localeCompare(a.name)); // newest first
    if (!scanFiles.length) {
      toast.error('No scan_*.csv files found in the scans folder.');
      return;
    }
    if (!stateFile) {
      toast.info('No state file found — syncing from CSV only (attachment metadata unavailable).');
    }
    const extraCsvFiles = scanFiles.slice(1).reverse()
      .map(f => ({ name: f.name, file: f }));
    if (extraCsvFiles.length > 0) {
      toast.info(`Merging ${scanFiles.length} scan files for full history sync.`, { duration: 4000 });
    }
    await runSync(stateFile, scanFiles[0], scanFiles[0].name, files, extraCsvFiles);
  }, [runSync, isConsultant, selectedClientId, resolveClientCode]);

  // Chromium / Brave — native folder picker
  const handleFolderSync = useCallback(async () => {
    if (syncing) return;
    // Consultants MUST pick a client first — the per-client eBuyExtract layout
    // (scans/<CODE>/) can't be resolved otherwise, and opps need a client org.
    if (isConsultant && !selectedClientId) {
      toast.error('Select a client organization before syncing eBuy.', { duration: 6000 });
      return;
    }
    const clientCode = await resolveClientCode();
    if (isConsultant && selectedClientId && !clientCode) {
      toast.error(
        'This client has no eBuy Folder Code. Set the 3-char code on the Client Profile '
        + '(must match the eBuyExtract scans/<CODE>/ folder), then sync again.',
        { duration: 10000 },
      );
      return;
    }

    if (typeof window.showDirectoryPicker === 'function') {
      try {
        const dirHandle = await window.showDirectoryPicker({ mode: 'read' });

        // state file — prefer per-client state_<CODE>.json, fall back to state.json
        let stateFile = null;
        const stateNames = clientCode ? [`state_${clientCode}.json`, 'state.json'] : ['state.json'];
        for (const sn of stateNames) {
          try {
            const h = await dirHandle.getFileHandle(sn);
            stateFile = await h.getFile();
            break;
          } catch { /* try next */ }
        }
        if (!stateFile) {
          toast.info('No state file found — syncing from CSV only (attachment metadata unavailable).');
        }

        let csvFile, csvName, extraCsvFiles = [];
        try {
          const scansRoot = await dirHandle.getDirectoryHandle('scans');
          // New layout: scans/<CODE>/scan_*.csv. Fall back to flat scans/scan_*.csv.
          let scansDir = scansRoot;
          if (clientCode) {
            try {
              scansDir = await scansRoot.getDirectoryHandle(clientCode);
            } catch {
              scansDir = scansRoot; // flat-layout fallback
            }
          }
          const csvEntries = [];
          for await (const [name, handle] of scansDir) {
            if (handle.kind === 'file' && name.startsWith('scan_') && name.endsWith('.csv')) {
              csvEntries.push([name, handle]);
            }
          }
          if (!csvEntries.length) {
            throw new Error(
              clientCode
                ? `No scan_*.csv files in scans/${clientCode}/ (or scans/). Check the eBuy Folder Code.`
                : 'No scan_*.csv files in scans/',
            );
          }
          // Sort newest-first: latest is the primary scan, others merged as history
          csvEntries.sort((a, b) => b[0].localeCompare(a[0]));
          const [latestName, latestHandle] = csvEntries[0];
          csvFile = await latestHandle.getFile();
          csvName = latestName;
          const olderEntries = csvEntries.slice(1).reverse();
          for (const [name, handle] of olderEntries) {
            const file = await handle.getFile();
            extraCsvFiles.push({ name, file });
          }
          if (extraCsvFiles.length > 0) {
            toast.info(`Merging ${csvEntries.length} scan files for full history sync.`, { duration: 4000 });
          }
        } catch (err) {
          toast.error(`Could not read scan CSV: ${err.message}`);
          return;
        }
        await runSync(stateFile, csvFile, csvName, dirHandle, extraCsvFiles);
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error(`Folder picker error: ${err.message}`);
        }
      }
    } else {
      fileInputRef.current?.click();
    }
  }, [syncing, runSync, isConsultant, selectedClientId, resolveClientCode]);

  useImperativeHandle(ref, () => ({
    triggerSync: handleFolderSync,
    refresh: fetchOpps,
    supportsFsApi: true,
  }), [handleFolderSync, fetchOpps]);

  useEffect(() => { onSyncStateChange?.(syncing); },     [syncing, onSyncStateChange]);
  useEffect(() => { onLatestSyncChange?.(latestSync); }, [latestSync, onLatestSyncChange]);

  // ── Attachment download ────────────────────────────────────────────────────
  const handleAttachmentDownload = useCallback(async (att) => {
    try {
      const res = await fetch(`${API_URL}/api/ebuy/attachments/${att.gridfs_id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name || att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download attachment.');
    }
  }, [authToken]);

  // ── AI Rank ───────────────────────────────────────────────────────────────
  const handleRankEbuy = useCallback(async () => {
    const orgId = targetOrgId || user?.organization_id;
    if (!orgId) return;
    setRankingEbuy(true);
    try {
      const res = await fetch(`${API_URL}/api/ebuy/opportunities/ai-rank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ organization_id: orgId }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || `Ranked ${data.ranked} RFQs`);
        // Re-fetch to pick up ai_rank fields
        const refreshRes = await fetch(
          `${API_URL}/api/ebuy/opportunities?${orgId ? `organization_id=${orgId}&` : ''}limit=1500`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (refreshRes.ok) {
          const refreshData = await refreshRes.json();
          if (Array.isArray(refreshData.opportunities)) setOpps(refreshData.opportunities);
          else if (Array.isArray(refreshData)) setOpps(refreshData);
        }
      } else {
        toast.error(data.detail || 'Ranking failed');
      }
    } catch (e) {
      toast.error('Ranking request failed');
    } finally {
      setRankingEbuy(false);
    }
  }, [authToken, targetOrgId, user]);

  // ── Claim / Dismiss ────────────────────────────────────────────────────────
  const handleClaim = useCallback(async (rfqId) => {
    try {
      const res = await fetch(`${API_URL}/api/ebuy/opportunities/${rfqId}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const msg = await readFetchError(res, `HTTP ${res.status}`);
        throw new Error(msg);
      }
      const body = await res.json();
      toast.success('Claimed — pursuit created.');
      window.location.href = `/${user?.workspaceId || user?.workspaceSlug || ''}/pursuit/${body.pursuit_id}/intake`;
    } catch (e) {
      toast.error(describeApiError(e, 'Claim failed'));
    }
  }, [authToken, user]);

  const handleDismiss = useCallback(async (rfqId) => {
    if (!window.confirm('Dismiss this RFQ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/ebuy/opportunities/${rfqId}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const msg = await readFetchError(res, `HTTP ${res.status}`);
        throw new Error(msg);
      }
      toast.success('Dismissed.');
      setSelectedRfq(null);
      await fetchOpps();
    } catch (e) {
      toast.error(describeApiError(e, 'Dismiss failed'));
    }
  }, [authToken, fetchOpps]);

  // ── Filter + Sort ──────────────────────────────────────────────────────────
  const filteredOpps = (() => {
    const filtered = opps.filter((rfq) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        (rfq.title || '').toLowerCase().includes(q) ||
        (rfq.request_id || '').toLowerCase().includes(q) ||
        (rfq.buyer_agency || '').toLowerCase().includes(q)
      );
    });
    const TIER_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    return [...filtered].sort((a, b) => {
      let av = a[eBuySortField], bv = b[eBuySortField];
      if (eBuySortField === 'match_tier') { av = TIER_ORDER[av] ?? 99; bv = TIER_ORDER[bv] ?? 99; }
      else if (eBuySortField === 'ai_rank') { av = AI_RANK_ORDER[av] ?? 99; bv = AI_RANK_ORDER[bv] ?? 99; }
      else if (eBuySortField === 'request_close') { av = av ? new Date(av).getTime() : Infinity; bv = bv ? new Date(bv).getTime() : Infinity; }
      else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
      if (av < bv) return eBuySortDir === 'asc' ? -1 : 1;
      if (av > bv) return eBuySortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const toggleEBuySort = (field) => {
    if (eBuySortField === field) setEBuySortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setEBuySortField(field); setEBuySortDir('asc'); }
  };

  const EBuySortIcon = ({ field }) => {
    if (eBuySortField !== field) return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return eBuySortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 text-cyan-600" />
      : <ChevronDown className="w-3 h-3 ml-1 text-cyan-600" />;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
    <div
      className="flex-1 flex flex-col overflow-hidden"
      data-testid="ebuy-tab"
      style={clientBrandColors ? {
        '--client-primary':    clientBrandColors.primary    || '#0ea5e9',
        '--client-secondary':  clientBrandColors.secondary  || '#0284c7',
        '--client-accent':     clientBrandColors.accent     || '#38bdf8',
      } : undefined}
    >

      {/* Filter bar — mirrors SAM Sentinel */}
      <div className="flex-shrink-0 px-5 py-2 border-b border-slate-100 flex items-center gap-2" data-testid="ebuy-filter-bar">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input
            placeholder="Search RFQs, agency…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 w-64 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs"
            data-testid="ebuy-search-input"
          />
        </div>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="h-8 w-28 bg-white border-slate-200 text-slate-900 text-xs" data-testid="ebuy-tier-filter">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="HIGH">Gold</SelectItem>
            <SelectItem value="MEDIUM">Silver</SelectItem>
            <SelectItem value="LOW">Bronze</SelectItem>
          </SelectContent>
        </Select>
        <Select value={aiRankFilter} onValueChange={setAiRankFilter}>
          <SelectTrigger className="h-8 w-32 bg-white border-slate-200 text-slate-900 text-xs" data-testid="ebuy-ai-rank-filter">
            <SelectValue placeholder="AI Fit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All AI Fit</SelectItem>
            {Object.entries(AI_RANK_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </span>
              </SelectItem>
            ))}
            <SelectItem value="unranked">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full border border-slate-300" />
                Unranked
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-28 bg-white border-slate-200 text-slate-900 text-xs" data-testid="ebuy-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="NEW">New</SelectItem>
            <SelectItem value="UPDATED">Updated</SelectItem>
            <SelectItem value="GONE">Closed</SelectItem>
            <SelectItem value="DISMISSED">Dismissed</SelectItem>
            <SelectItem value="CLAIMED">Claimed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <span className="text-[11px] text-slate-400" data-testid="ebuy-count-badge">
          {filteredOpps.length} RFQ{filteredOpps.length !== 1 ? 's' : ''}
        </span>
        {/* AI Rank button */}
        <button
          onClick={handleRankEbuy}
          disabled={rankingEbuy}
          className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50 transition-colors"
          title="AI-score each open RFQ against this client's portfolio"
          data-testid="ai-rank-ebuy-btn"
        >
          {rankingEbuy
            ? <><Loader2 className="w-3 h-3 animate-spin" />Ranking…</>
            : <><Brain className="w-3 h-3" />AI Rank</>}
        </button>
        <button
          onClick={fetchOpps}
          className="text-slate-500 hover:text-cyan-600 transition-colors p-1"
          title="Refresh"
          data-testid="ebuy-refresh-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* File-upload progress strip — only visible during Phase 2 */}
      {syncPhase === 'files' && fileProgress && (
        <div className="flex-shrink-0 px-5 py-1.5 bg-cyan-50 border-b border-cyan-200 flex items-center gap-2">
          <Loader2 className="w-3 h-3 text-cyan-600 animate-spin flex-shrink-0" />
          <span className="text-[11px] text-cyan-700">{fileProgress}</span>
          <span className="text-[10px] text-slate-500 ml-auto">Files are stored in the cloud — your team can download them without eBuy access.</span>
        </div>
      )}

      {/* Table + detail pane */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left column: scrollable table + persistent footer */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
              </div>
            ) : filteredOpps.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center p-6" data-testid="ebuy-empty-state">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                  <Archive className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-400 font-medium text-sm">No eBuy RFQs</p>
                <p className="text-slate-500 text-xs mt-1 max-w-[280px]">
                  {statusFilter === 'NEW'
                    ? 'Click "Sync eBuy folder" to import your scraper output.'
                    : 'Try adjusting your filters.'}
                </p>
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200 sticky top-0 bg-white z-10">
                    {[
                      { label: 'AI Fit',               field: 'ai_rank',        cls: 'w-[72px] text-left' },
                      { label: 'Closes',               field: 'request_close',  cls: 'w-[115px] text-left' },
                      { label: 'Tier',                 field: 'match_tier',     cls: 'w-[88px] text-left' },
                      { label: 'Title · Agency · Ref', field: 'title',          cls: 'text-left' },
                      { label: 'SIN',                  field: 'category',       cls: 'w-[72px] text-left' },
                      { label: 'Contract',             field: 'contract_number', cls: 'w-[128px] text-left' },
                      { label: 'Status',               field: 'status',         cls: 'w-[90px] text-left' },
                    ].map(({ label, field, cls }) => (
                      <th key={label}
                        className={cn('px-4 py-2.5', cls, 'cursor-pointer select-none hover:text-slate-700')}
                        onClick={() => toggleEBuySort(field)}
                      >
                        <span className="inline-flex items-center">
                          {label}<EBuySortIcon field={field} />
                        </span>
                      </th>
                    ))}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {filteredOpps.map(rfq => (
                    <EbuyTableRow
                      key={rfq.id}
                      rfq={rfq}
                      isSelected={selectedRfq?.id === rfq.id}
                      onSelect={() => setSelectedRfq(prev => prev?.id === rfq.id ? null : rfq)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Persistent count footer — always visible, never buried in scroll */}
          {!loading && opps.length > 0 && (
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-white">
              <span className="text-xs text-slate-500">
                Showing <span className="font-medium text-slate-700">{filteredOpps.length}</span>
                {filteredOpps.length !== opps.length && (
                  <> of <span className="font-medium text-slate-700">{opps.length}</span> total</>
                )} RFQ{filteredOpps.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Detail pane — 460 px, shown on row click */}
        {selectedRfq && (
          <div
            className="w-[460px] flex-shrink-0 border-l border-slate-200 overflow-y-auto"
            data-testid="ebuy-detail-pane"
          >
            <EbuyRfqDetail
              rfq={selectedRfq}
              onClaim={handleClaim}
              onDismiss={handleDismiss}
              onClose={() => setSelectedRfq(null)}
              onAttachmentDownload={handleAttachmentDownload}
              brandColors={clientBrandColors}
            />
          </div>
        )}
      </div>
    </div>

    {/* Firefox / Safari fallback for folder sync (hidden) */}
    <input
      ref={fileInputRef}
      type="file"
      webkitdirectory="true"
      directory=""
      multiple
      style={{ display: 'none' }}
      onChange={handleFileInputChange}
    />
    </>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// EbuyTableRow
// ─────────────────────────────────────────────────────────────────────────────
function EbuyTableRow({ rfq, isSelected, onSelect }) {
  const tier    = TIER_BADGE[rfq.match_tier] || TIER_BADGE.LOW;
  const TierIcon = tier.icon;
  const daysUntil = rfq.request_close
    ? Math.ceil((new Date(rfq.request_close).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const isUrgent  = daysUntil !== null && daysUntil <= 7;
  const isWarning = daysUntil !== null && daysUntil > 7 && daysUntil <= 14;
  // If backend says GONE but close date is still in the future, the opp is still
  // open — it just wasn't in the latest scrape.  Don't label it "Closed".
  const effectiveStatus = (rfq.status === 'GONE' && daysUntil !== null && daysUntil >= 0)
    ? 'NEW'
    : rfq.status;
  const chip = STATUS_CHIP[effectiveStatus];

  return (
    <tr
      onClick={onSelect}
      className={cn(
        'border-b border-slate-100 cursor-pointer transition-colors group',
        isSelected ? 'bg-slate-100' : 'hover:bg-slate-50',
      )}
      data-testid={`ebuy-rfq-row-${rfq.request_id}`}
    >
      {/* AI Fit rank */}
      <td className={cn('px-4 py-3 w-[72px]', isSelected && 'border-l-2 border-l-cyan-500')}>
        {rfq.ai_rank ? (
          <Badge
            className={cn('text-[10px] font-semibold w-fit cursor-default', AI_RANK_CONFIG[rfq.ai_rank]?.cls)}
            title={`AI Fit: ${rfq.ai_rank}${rfq.ai_rank_score != null ? ` (${rfq.ai_rank_score}%)` : ''} — ${AI_RANK_CONFIG[rfq.ai_rank]?.tip}${rfq.ai_rank_reasoning ? '\n' + rfq.ai_rank_reasoning : ''}`}
          >
            {AI_RANK_CONFIG[rfq.ai_rank]?.label}
          </Badge>
        ) : (
          <span className="text-slate-300 text-xs" title="Not yet AI-ranked">—</span>
        )}
      </td>
      {/* Close date */}
      <td className="px-4 py-3 w-[115px]">
        {rfq.request_close ? (
          <>
            <div className={cn('font-medium text-xs tabular-nums',
              isUrgent ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-600')}>
              {new Date(rfq.request_close).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
            {daysUntil !== null && (
              <div className={cn('text-[10px] mt-0.5',
                isUrgent ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-500')}>
                {daysUntil < 0 ? 'Closed' : daysUntil === 0 ? 'Today!' : `${daysUntil}d left`}
              </div>
            )}
          </>
        ) : <span className="text-slate-400 text-xs">—</span>}
      </td>

      {/* Tier badge */}
      <td className="px-4 py-3">
        <Badge
          className={cn('text-[10px] font-medium flex items-center gap-1 w-fit', tier.badgeClass)}
          data-testid={`ebuy-tier-badge-${rfq.request_id}`}
        >
          <TierIcon className="w-3 h-3" />
          {tier.label}
        </Badge>
      </td>

      {/* Title + Agency + RFQ # */}
      <td className="px-4 py-3 min-w-0">
        <div className="text-slate-900 font-medium text-sm leading-snug truncate" title={rfq.title}>
          {rfq.title || rfq.request_id}
          {rfq.is_mras && (
            <span className="ml-1.5 text-[10px] font-normal text-amber-600">RFI</span>
          )}
        </div>
        <div className="text-slate-500 text-[11px] mt-0.5 truncate">{rfq.buyer_agency || '—'}</div>
        {rfq.request_id && (
          <div className="text-slate-400 font-mono text-[10px] mt-0.5 truncate">{rfq.request_id}</div>
        )}
      </td>

      {/* SIN */}
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-slate-500">{rfq.category || '—'}</span>
      </td>

      {/* Contract # */}
      <td className="px-4 py-3">
        <span className="font-mono text-[11px] text-slate-500">
          {rfq.contract_number || rfq.award_key || '—'}
        </span>
      </td>

      {/* Status chip (hidden for plain NEW to reduce noise) */}
      <td className="px-4 py-3">
        {chip && rfq.status !== 'NEW' && (
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium border', chip.cls)}>
            {chip.label}
          </span>
        )}
      </td>

      {/* Chevron */}
      <td className="px-2 py-3">
        <ChevronRight className={cn('w-4 h-4 transition-colors',
          isSelected ? 'text-cyan-600' : 'text-slate-700 group-hover:text-slate-400')} />
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EbuyRfqDetail — right-panel detail view
// ─────────────────────────────────────────────────────────────────────────────
function EbuyRfqDetail({ rfq, onClaim, onDismiss, onClose, onAttachmentDownload, brandColors }) {
  const tier     = TIER_BADGE[rfq.match_tier] || TIER_BADGE.LOW;
  const TierIcon = tier.icon;
  const isClaimable = !rfq.claimed_pursuit_id && rfq.status !== 'DISMISSED';
  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';
  const daysUntil = rfq.request_close
    ? Math.ceil((new Date(rfq.request_close).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="p-5" data-testid="ebuy-rfq-detail">

      {/* ── Close / header bar ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0', tier.iconBg)}>
            <TierIcon className="w-3.5 h-3.5" />
          </div>
          <Badge className={cn('text-[10px] font-medium', tier.badgeClass)}>
            {tier.label} · {rfq.match_score || 0}%
          </Badge>
          <Badge className="text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">
            {rfq.status || 'NEW'}
          </Badge>
          {rfq.ai_rank && (
            <Badge
              className={cn('text-[10px] font-semibold', AI_RANK_CONFIG[rfq.ai_rank]?.cls)}
              title={rfq.ai_rank_reasoning || AI_RANK_CONFIG[rfq.ai_rank]?.tip}
            >
              <Brain className="w-2.5 h-2.5 mr-0.5" />
              {AI_RANK_CONFIG[rfq.ai_rank]?.label}
              {rfq.ai_rank_score != null && ` · ${rfq.ai_rank_score}%`}
            </Badge>
          )}
          {rfq.is_mras && (
            <Badge className="text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200">RFI</Badge>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-md flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100/60 transition-colors flex-shrink-0"
          data-testid="ebuy-close-detail-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Title + match reason */}
      <h2 className="text-base font-semibold text-slate-900 leading-snug mb-1">{rfq.title}</h2>
      {rfq.match_reason && (
        <p className="text-cyan-600 text-xs mb-1">{rfq.match_reason}</p>
      )}
      {rfq.ai_rank_reasoning && (
        <p className="text-purple-700 text-xs mb-3 flex items-start gap-1.5">
          <Brain className="w-3 h-3 mt-0.5 flex-shrink-0 text-purple-500" />
          {rfq.ai_rank_reasoning}
        </p>
      )}
      {!rfq.ai_rank_reasoning && <div className="mb-3" />}

      {/* ── Action buttons ──────────────────────────────────────────────── */}
      {isClaimable && (
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => onClaim(rfq.id)}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white h-9 text-sm"
            data-testid={`ebuy-claim-btn-${rfq.request_id}`}
          >
            <CircleCheck className="w-4 h-4 mr-1.5" /> Claim Pursuit
          </Button>
          <Button
            onClick={() => onDismiss(rfq.id)}
            className="bg-red-600 hover:bg-red-700 text-white h-9 px-4"
            data-testid={`ebuy-dismiss-btn-${rfq.request_id}`}
          >
            <OctagonX className="w-4 h-4 mr-1.5" /> Dismiss
          </Button>
        </div>
      )}
      {rfq.claimed_pursuit_id && (
        <div className="mb-4">
          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Claimed to pursuit pipeline
          </Badge>
        </div>
      )}

      {/* ── Key info grid ────────────────────────────────────────────────── */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3">
        <div className="px-3.5 py-2.5 border-b border-slate-200 text-xs font-medium text-slate-600">Details</div>
        <div className="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Closes
            </div>
            <div className={cn('text-xs font-semibold',
              daysUntil !== null && daysUntil <= 7  ? 'text-red-600'
              : daysUntil !== null && daysUntil <= 14 ? 'text-amber-600' : 'text-slate-700')}>
              {formatDate(rfq.request_close)}
              {daysUntil !== null && daysUntil >= 0 && (
                <span className="text-[10px] font-normal ml-1 opacity-70">({daysUntil}d)</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Posted
            </div>
            <div className="text-xs text-slate-700">{formatDate(rfq.request_posted)}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Buyer Agency
            </div>
            <div className="text-xs text-slate-700">{rfq.buyer_agency || '—'}</div>
            {rfq.buyer_poc_name && (
              <div className="text-[10px] text-slate-500 mt-0.5">{rfq.buyer_poc_name}</div>
            )}
          </div>
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <Shield className="w-3 h-3" /> SIN / Contract
            </div>
            <div className="text-xs font-mono text-slate-700">{rfq.category || '—'}</div>
            {rfq.contract_number && (
              <div className="text-[10px] font-mono text-slate-500 mt-0.5">{rfq.contract_number}</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Request ID + eBuy link ───────────────────────────────────────── */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3 p-3.5 flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] text-slate-500 mb-0.5">Request ID</div>
          <div className="text-xs font-mono text-slate-700">{rfq.request_id}</div>
        </div>
        {rfq.ebuy_url && (
          <a
            href={rfq.ebuy_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1.5 flex-shrink-0"
            data-testid={`ebuy-source-link-${rfq.request_id}`}
          >
            Open in eBuy <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* ── Q&A + Amendments ────────────────────────────────────────────── */}
      {(rfq.has_qa_activity || rfq.amendments_blob) && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3 p-3.5">
          <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Activity</div>
          {rfq.has_qa_activity && (
            <div className="flex items-start gap-2 text-xs text-slate-600 mb-1.5">
              <span className="text-cyan-600 mt-0.5 flex-shrink-0">Q&amp;A</span>
              <span>Activity on this RFQ — check the eBuy portal for Q&amp;As.</span>
            </div>
          )}
          {rfq.amendments_blob && (
            <div className="flex items-start gap-2 text-xs text-slate-600">
              <AlertCircle className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-purple-700 mb-0.5">Amendments posted</div>
                <div className="text-[10px] text-slate-600 whitespace-pre-wrap">{rfq.amendments_blob}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── eBuy attachments ─────────────────────────────────────────────── */}
      {!rfq.is_mras && (() => {
        const ebuyAtts = rfq.ebuy_attachments?.length > 0
          ? rfq.ebuy_attachments
          : (rfq.attachments || []);
        if (!ebuyAtts.length) return null;
        return (
          <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3 p-3.5">
            <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> eBuy Attachments ({ebuyAtts.length})
            </div>
            <div className="space-y-1.5">
              {ebuyAtts.map((att, i) => {
                const hasFile = !!att.gridfs_id;
                const label   = att.file_name || att.filename || `Attachment ${i + 1}`;
                return (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <FileText className={cn('w-3 h-3 flex-shrink-0', hasFile ? 'text-slate-600' : 'text-slate-400')} />
                    {hasFile ? (
                      <button
                        type="button"
                        onClick={() => onAttachmentDownload(att)}
                        className="truncate text-cyan-600 hover:text-cyan-700 hover:underline text-left"
                      >
                        {label}
                      </button>
                    ) : (
                      <a
                        href={rfq.ebuy_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="File not yet uploaded — click to view on eBuy"
                        className="truncate text-slate-400 hover:text-slate-400 hover:underline"
                      >
                        {label}
                      </a>
                    )}
                    {att.size_bytes > 0 && (
                      <span className="text-[10px] text-slate-500 ml-auto flex-shrink-0">
                        {Math.round(att.size_bytes / 1024)} KB
                      </span>
                    )}
                    {!hasFile && (
                      <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">eBuy ↗</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── MRAS section ─────────────────────────────────────────────────── */}
      {rfq.is_mras && (
        <div className="bg-slate-50 rounded-lg border border-purple-200 mb-3 p-3.5">
          <div className="text-[10px] font-semibold text-purple-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Archive className="w-3 h-3" /> MRAS — Market Research
          </div>
          {rfq.mras_link && (
            <a
              href={rfq.mras_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1.5 mb-2"
            >
              Open MRAS Survey <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {(() => {
            const allFiles = [
              ...(rfq.mras_attachments || []),
              ...(rfq.ebuy_attachments || []),
              ...(rfq.attachments || []),
            ];
            if (!allFiles.length) {
              return (
                <p className="text-[10px] text-slate-500">
                  No uploaded documents found. Re-sync to pick up attachments.
                </p>
              );
            }
            return (
              <div className="space-y-1.5">
                {allFiles.map((att, i) => {
                  const hasFile = !!att.gridfs_id;
                  const label   = att.file_name || `MRAS Doc ${i + 1}`;
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                      <FileText className={cn('w-3 h-3 flex-shrink-0', hasFile ? 'text-purple-600' : 'text-slate-400')} />
                      {hasFile ? (
                        <button
                          type="button"
                          onClick={() => onAttachmentDownload(att)}
                          className="truncate text-purple-600 hover:text-purple-700 hover:underline text-left"
                        >
                          {label}
                        </button>
                      ) : (
                        <a
                          href={rfq.ebuy_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="File not yet uploaded — click to view on eBuy"
                          className="truncate text-slate-400 hover:text-slate-400 hover:underline"
                        >
                          {label}
                        </a>
                      )}
                      {att.size_bytes > 0 && (
                        <span className="text-[10px] text-slate-500 ml-auto flex-shrink-0">
                          {Math.round(att.size_bytes / 1024)} KB
                        </span>
                      )}
                      {!hasFile && (
                        <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">eBuy ↗</span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
